import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
import { getRoleThemeClasses } from '../../logic/uiHelpers';
import { dataLayer } from '../../../data/dataLayer';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  totalSteps?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  totalSteps = 2,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const theme = getRoleThemeClasses(role);
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Ik geef je graag een rondleiding door de app.`
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-108" data-mp="1ygzwjjhh8fcc">
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-6 relative" data-id="element-109" data-mp="2m7cfdpe9fwhp">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-110" data-mp="2m6199ixcecnf">
          <DjayNarrator emotion={config.emotion} size="lg" data-id="element-111" data-mp="pazppoz4m6ul" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-112" data-mp="ajqpklxll337">
          <DjayBubble message={config.message} position="top" colorScheme={theme.colorScheme} autoDismissMs={0} data-id="element-113" data-mp="fik32ecvwu3d" />
        </div>
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${theme.border} relative overflow-hidden`} data-id="element-114" data-mp="5lyyfu4s1l02">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${theme.gradient}`} data-id="element-115" data-mp="3nsd9nlbnvgur" />

        <OnboardingProgress currentStep={0} totalSteps={totalSteps} role={role} data-id="element-116" data-mp="3srgi7fquo57h" />

        {role === 'learner' && <div className="space-y-6" data-id="element-117" data-mp="1qx7d0vpkawjt">
            <div className="text-center" data-id="element-118" data-mp="1qw616p0q2611">
              <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-119" data-mp="3bfhzax6ct2pq">
                Klaar voor de start? 🚀
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-120" data-mp="36iriu8qn053s">
                Je gaat oefenen op het niveau van{' '}
                <span className="font-bold text-orange-600" data-id="element-121" data-mp="3l7fd8boqy8s5">
                  groep {groupLevel}
                </span>
                .
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 space-y-4" data-id="element-122" data-mp="3u5hjb5nc7rma">
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-123" data-mp="3p6o6lffvk4n6">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-124" data-mp="24se1uv4z3umn">
                  <span className="text-xl" data-id="element-125" data-mp="vue7v5jpi0do">🗺️</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-126" data-mp="10t7kkvr65ncs">
                  Ontdek waar je alles kunt vinden in de app
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-127" data-mp="19uf7kho8j1rk">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-128" data-mp="m39to81uujjw">
                  <span className="text-xl" data-id="element-129" data-mp="3qk49jehhmkfb">⭐</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-130" data-mp="3ukwi05ijclaj">
                  Zie hoe je XP en nieuwe looks verdient
                </p>
              </motion.div>
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-131" data-mp="3i3ls9z3zn1dp">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-132" data-mp="3i0hgafka71ch">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-133" data-mp="391vxcjawdkb6">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-134" data-mp="391443f4rvyo2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-135" data-mp="342sun5ie3q6d">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-136" data-mp="2zb11cn7un6cj" />
                </div>
                <span data-id="element-137" data-mp="2udrftinkzetz">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-138" data-mp="2qbea2u8u3gx7">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-139" data-mp="2q91kbuwu429l">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-140" data-mp="2gcyhsny2m1m4" />
                </div>
                <span data-id="element-141" data-mp="2gfay6wo6k3x2">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-142" data-mp="26nrm5y2zbdgv">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-143" data-mp="2bopaytihv5rc">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-144" data-mp="1wsgefzpzn6im" />
                </div>
                <span data-id="element-145" data-mp="21rauwv0hgizk">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-146" data-mp="2pi6jzkdk1034">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-147" data-mp="2uf6r7ym548vt">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-148" data-mp="2fp96jsnf61bd">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-149" data-mp="2fo6gfmxx82wi">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-150" data-mp="2knaryk8bicq0">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-151" data-mp="38clnnnat9gxa" />
                </div>
                <span data-id="element-152" data-mp="3d7hp1e1xtgn2">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-153" data-mp="11p4r7ag5ncqf">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-154" data-mp="wruv5by9g2tm">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-155" data-mp="1bf7h31rzcakj" />
                </div>
                <span data-id="element-156" data-mp="16gmj7907a2z9">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-157" data-mp="16jif6rshmhl2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-158" data-mp="is3j3grb0wi9">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-159" data-mp="dul9rp9dkf9q" />
                </div>
                <span data-id="element-160" data-mp="rtky0vsz6s5d">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8 flex flex-col gap-3" data-id="element-161" data-mp="nsr8xz3sr7aw">
          {role === 'learner' ? <>
              <Button onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: true,
                tourCompleted: false
              });
            } catch (e) {}
            onNext();
          }} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-162" data-mp="239hvj8evqke7">
                Start de rondleiding
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-163" data-mp="f94e4vqxzg5y" />
              </Button>
              <Button variant="ghost" onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: false,
                tourCompleted: true
              });
            } catch (e) {}
            onNext();
          }} className="w-full text-slate-500 hover:text-slate-700" data-id="element-164" data-mp="k7phv55ekhz5">
                Sla over
              </Button>
            </> : <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-165" data-mp="18ac8zmcxkq80">
              Verder
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-166" data-mp="zctkg802vtrd" />
            </Button>}
        </div>
      </div>
    </motion.div>;
}